import { Controller } from "stimulus"
import Trix from "trix"
import DOMPurify from "dompurify"

const ACCEPTED_CONTENT_TYPES = [
  "application/pdf",
  "image/bmp",
  "image/gif",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/tiff",
  "image/webp",
  "image/x-ms-bmp"
];

const FILE_PICKER_ACCEPT = "image/*,application/pdf";

export default class extends Controller {
  connect() {
    this.configureFilePicker();

    this.editor = this.element.querySelector("trix-editor");
    this.submitButton = this.element.querySelector('[type="submit"]');
    this.status = this.element.querySelector(".chat-composer-status");
    if (!this.editor) return;
    this.sendWithEnterQuery = window.matchMedia("(min-width: 768px) and (pointer: fine)");
    this.onInputModeChange = () => this.updateSubmit();
    this.sendWithEnterQuery.addEventListener("change", this.onInputModeChange);
    this.workspace = this.element.closest(".chat-workspace");
    this.optimistic = this.element.closest('[data-composer-mode]')?.dataset.composerMode !== "edit";
    this.onBefore = () => {
      let token = this.element.querySelector('[name="client_message_id"]');
      if (!token) {
        token = document.createElement("input");
        token.type = "hidden";
        token.name = "client_message_id";
        this.element.appendChild(token);
      }
      token.value = window.crypto.randomUUID();
    };

    this.onFileAccept = event => {
      if (this.acceptedFile(event.file)) return;
      event.preventDefault();
      this.showStatus("Choisissez une image ou un fichier PDF.");
    };
    this.onChange = () => this.updateSubmit();
    this.onKeydown = event => {
      if (event.key !== "Enter" || event.shiftKey || event.isComposing || event.keyCode === 229) return;
      // Sur mobile, Entrée conserve le retour à la ligne.
      if (!this.sendWithEnterQuery.matches) return;
      event.preventDefault();
      if (!this.submitButton.disabled) this.element.requestSubmit(this.submitButton);
    };
    this.onBeforeSend = event => {
      if (this.sending || !this.hasContent() || this.uploading()) {
        event.preventDefault();
        return;
      }
      this.sending = true;
      if (this.optimistic && this.workspace) {
        this.pendingId = this.element.querySelector('[name="client_message_id"]').value;
        this.sentHTML = this.editor.inputElement.value;
        this.workspace.dispatchEvent(new CustomEvent("chat:pending", { detail: {
          id: this.pendingId,
          html: DOMPurify.sanitize(this.editor.innerHTML),
          parentId: this.element.querySelector('[name="submessage[message_header_id]"]')?.value
        } }));
        // Rails UJS a déjà sérialisé le formulaire : vider Trix ne change pas la requête.
        this.editor.editor.loadHTML("");
      } else {
        this.editor.setAttribute("contenteditable", "false");
      }
      this.updateSubmit();
      this.showStatus("");
    };
    this.onSuccess = () => {
      if (this.pendingElement()) return this.onError();
      if (!this.optimistic) this.editor.editor.loadHTML("");
      this.showStatus("");
    };
    this.onError = event => {
      const errors = event?.detail?.[0]?.errors;
      const errorMessage = Array.isArray(errors) ? errors.join(" ") : "L’envoi a échoué. Vous pouvez réessayer.";
      if (this.optimistic) {
        const pending = this.pendingElement();
        // La confirmation WebSocket peut précéder une erreur de la réponse HTTP.
        if (!pending) return;
        if (!this.element.isConnected) {
          pending.remove(); // le formulaire d'erreur rendu par Rails contient le texte
          return;
        }
        const failedHTML = this.sentHTML;
        const restore = () => {
          if (!this.element.isConnected) return;
          if (this.hasContent()) {
            this.showStatus("Votre nouveau brouillon est conservé. Envoyez-le ou effacez-le avant de reprendre le message non envoyé.");
            return;
          }
          this.editor.editor.loadHTML(failedHTML);
          pending.remove();
          this.editor.focus();
          this.showStatus(errorMessage);
        };
        if (!this.hasContent()) restore();
        else {
          const label = pending.querySelector(".chat-pending-status");
          label.textContent = "Non envoyé · ";
          const retry = document.createElement("button");
          retry.type = "button";
          retry.textContent = "Reprendre le message";
          retry.addEventListener("click", restore);
          label.appendChild(retry);
          pending.classList.add("message--failed");
        }
      } else this.showStatus("L’envoi a échoué. Votre message est conservé, vous pouvez réessayer.");
    };
    this.onComplete = () => {
      this.sending = false;
      this.editor.setAttribute("contenteditable", "true");
      this.updateSubmit();
    };
    this.editor.addEventListener("trix-file-accept", this.onFileAccept);
    this.editor.addEventListener("trix-change", this.onChange);
    this.editor.addEventListener("keydown", this.onKeydown);
    this.element.addEventListener("ajax:before", this.onBefore);
    this.element.addEventListener("ajax:beforeSend", this.onBeforeSend);
    this.element.addEventListener("ajax:success", this.onSuccess);
    this.element.addEventListener("ajax:error", this.onError);
    this.element.addEventListener("ajax:complete", this.onComplete);
    this.uploadObserver = new MutationObserver(this.onChange);
    this.uploadObserver.observe(this.editor, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-trix-attachment"] });
    this.updateSubmit();
    if (this.element.closest('[data-composer-mode]')?.dataset.composerMode !== "new") {
      this.focusFrame = requestAnimationFrame(() => this.editor.focus());
    }
  }

  pendingElement() {
    return this.pendingId && document.getElementById(`pending-${this.pendingId}`);
  }

  hasContent() {
    return this.editor.textContent.trim().length > 0 || !!this.editor.querySelector("[data-trix-attachment]");
  }

  uploading() {
    return this.editor.editor && this.editor.editor.getDocument().getAttachments().some(attachment => attachment.isPending());
  }

  updateSubmit() {
    const hasContent = this.hasContent();
    const showButton = !this.sendWithEnterQuery.matches && hasContent;
    this.submitButton.hidden = !showButton;
    this.element.classList.toggle("chat-composer--has-send-button", showButton);
    this.submitButton.disabled = !!this.sending || !hasContent || this.uploading();
  }

  showStatus(text) {
    if (this.status) this.status.textContent = text;
  }

  disconnect() {
    cancelAnimationFrame(this.focusFrame);
    if (this.editor) {
      this.sendWithEnterQuery.removeEventListener("change", this.onInputModeChange);
      this.editor.removeEventListener("trix-file-accept", this.onFileAccept);
      this.editor.removeEventListener("trix-change", this.onChange);
      this.editor.removeEventListener("keydown", this.onKeydown);
      this.element.removeEventListener("ajax:before", this.onBefore);
      this.element.removeEventListener("ajax:beforeSend", this.onBeforeSend);
      this.element.removeEventListener("ajax:success", this.onSuccess);
      this.element.removeEventListener("ajax:error", this.onError);
      this.element.removeEventListener("ajax:complete", this.onComplete);
      this.uploadObserver.disconnect();
    }
    const controllerClass = this.constructor;
    controllerClass.instanceCount = Math.max((controllerClass.instanceCount || 1) - 1, 0);

    if (controllerClass.instanceCount === 0 && controllerClass.originalPickFiles && Trix?.config?.input) {
      Trix.config.input.pickFiles = controllerClass.originalPickFiles;
      controllerClass.originalPickFiles = null;
    }
  }

  configureFilePicker() {
    const controllerClass = this.constructor;
    controllerClass.instanceCount = (controllerClass.instanceCount || 0) + 1;

    if (!Trix?.config?.input || controllerClass.originalPickFiles) return;

    controllerClass.originalPickFiles = Trix.config.input.pickFiles;

    Trix.config.input.pickFiles = callback => {
      const fileInput = document.createElement("input");
      const existingInput = document.getElementById(Trix.config.input.fileInputId);

      if (existingInput) {
        existingInput.remove();
      }

      fileInput.type = "file";
      fileInput.multiple = true;
      fileInput.hidden = true;
      fileInput.accept = FILE_PICKER_ACCEPT;
      fileInput.id = Trix.config.input.fileInputId;
      fileInput.addEventListener("change", () => {
        callback(fileInput.files);
        fileInput.remove();
      });

      document.body.appendChild(fileInput);
      fileInput.click();
    };
  }

  acceptedFile(file) {
    const filenameParts = (file.name || "").toLowerCase().split(".");
    const hasSingleExtension = filenameParts.length <= 2;

    return hasSingleExtension && ACCEPTED_CONTENT_TYPES.includes(file.type);
  }
}
